/**
 * Invitee Onboarding v1.0 copy (YAR-288 / YAR-289).
 *
 * Governed verbatim by the invitee copy page in the Copy Hub; the Admin Copy
 * Tracker governs the shared Hero strings (1.x rows), which live in
 * `onboardingCopy.ts` and are NOT duplicated here. Two global rules from the
 * Dev Notes are load-bearing for every string below:
 *
 *   1. Headlines carry no trailing period; sentence-style microcopy keeps its
 *      period. The three orientation-card titles are headlines (confirmed
 *      against Figma 9644:96879 — "Tasks and events land here automatically",
 *      no period), while the Acceptance title and the Welcome body are
 *      sentences and keep theirs.
 *   2. Apostrophes are typographic (’), matching ONBOARDING_COPY.
 *
 * Interpolated names are passed in rather than baked in so a household or
 * inviter that arrives blank falls back at the call site, not here.
 */

/** Shown when an invitation carries no usable inviter display name. */
export const INVITEE_FALLBACK_ADMIN_NAME = 'Your household admin';

/** Shown when an invitation carries no usable household name. */
export const INVITEE_FALLBACK_HOUSEHOLD_NAME = 'your household';

export const INVITEE_COPY = {
  /** Hero arrival + expired alerts (tracker rows 1.9/1.91 and 1.93/1.94). */
  hero: {
    arrivalTitle: (householdName: string) =>
      `You’re invited to ${householdName}`,
    arrivalBody: (adminName: string, inviteeEmail: string) =>
      `${adminName} invited you. Continue as ${inviteeEmail}.`,
    // Deliberately names NO household and NO inviter: an expired link may not
    // carry trustworthy metadata.
    expiredTitle: 'Invite link expired',
    // The duration is NOT written here (YAR-371). It used to be a hardcoded
    // copy of household-invitations' DEFAULT_INVITATION_EXPIRY_MINUTES and
    // drifted: when the default moved from 1 hour to a day, the invitee got an
    // email promising "24 hours" and then an expired screen claiming "1 hour".
    // The label now comes from invitations-preview (`expiry_label`), rendered
    // by the same `formatExpiry` off the same env resolution as the email.
    //
    // No label (older server, malformed response) → neutral copy that states
    // NO number, rather than a number that may be wrong.
    expiredBody: (expiryLabel?: string | null) => {
      const label = expiryLabel?.trim();
      return label
        ? `Invite links expire after ${label}. Ask your household admin for a new invite.`
        : 'This invite link has expired. Ask your household admin for a new invite.';
    },
    unavailableTitle: 'Invite link unavailable',
    // YAR-154 AC10 (Aug 11 update), verbatim.
    unavailableBody:
      'This invite was used or declined. Ask your household admin for a new invite.',
  },
  acceptance: {
    /**
     * NO trailing period. It reads like a sentence, so one was added — but
     * both frames set it as a headline without terminal punctuation
     * (Figma 9644:96875 Can edit / 9706:127456 Can view, verified on both).
     * QA reported the stray dot on YAR-289.
     */
    title: (adminName: string, householdName: string, canEdit: boolean) =>
      canEdit
        ? `${adminName} invited you to ${householdName} to add and update tasks and events`
        : `${adminName} invited you to ${householdName} to view tasks and events`,
    accept: 'Accept invite',
    decline: 'Decline',
  },
  declineModal: {
    title: 'Decline this invite?',
    body: (adminName: string) =>
      `You can ask ${adminName} for a new one later.`,
    /** "Decline", not "Decline invite" — Figma 9704:112731 (QA on YAR-288). */
    confirm: 'Decline',
    cancel: 'Cancel',
  },
  /**
   * Orientation carousel. Card 1 and card 3 are shared; card 2 varies by
   * access level.
   *
   * TITLE ONLY — these cards carry no supporting line. That is not an
   * omission: the invitee Demo frames were re-authored on 2026-08-11 and each
   * renders a headline directly above the recording, with nothing between
   * them. Read from the updated frames rather than inferred (Can view
   * 9706:127463 / 9706:127465 / 9706:127467, Can edit 9644:96879 / 9644:96881
   * / 9644:96883 — card 3 is byte-identical across the two sections).
   *
   * This resolves YAR-367, which asked Kelvin for §16.3's "real" supporting
   * line for the Can view card. There is none to supply — the answer is that
   * the line should not exist, so the stand-in written to keep that card
   * coherent ("Your household confirms what lands on the schedule.") is
   * deleted rather than replaced. It also matches the QA read on YAR-313:
   * card 2 had "an extra subheading".
   *
   * The admin flow is deliberately NOT changed to match: it has four cards
   * with headline + supporting line, and is governed by its own section. Kept
   * as separate literals from `ONBOARDING_COPY.carousel` for the same reason —
   * a shared constant would silently propagate one section's edit to the other.
   */
  carousel: {
    /**
     * CARD 2 SPLITS BY ACCESS LEVEL, per the two stories' AC 4:
     *
     *   Can edit  → "Add, edit, and complete tasks and events"  (YAR-288 §16.2)
     *   Can view  → "See everything in action, nothing to manage" (YAR-289 §16.3)
     *
     * Cards 1 and 3 are shared, and their titles now come from the frames too;
     * they previously read "Choose who matters" and "Share with your people",
     * which were carried over from the admin carousel and never matched the
     * invitee section (YAR-313).
     */
    cards: (canEdit: boolean) => [
      { title: 'Tasks and events land here automatically' },
      canEdit
        ? { title: 'Add, edit, and complete tasks and events' }
        : { title: 'See everything in action, nothing to manage' },
      { title: 'Every change shows up in Activity' },
    ],
    next: 'Next',
    continue: 'Continue',
  },
  welcome: {
    /** Exclamation mark is parity with the admin welcome. */
    title: (householdName: string) => `Welcome to Yaruta and ${householdName}!`,
    body: (householdName: string) =>
      `You’re all set. Tasks and events from ${householdName} are ready whenever you are.`,
    /** Title case — Dashboard is a title. */
    cta: 'Go to Dashboard',
  },
  /**
   * Backend failures never surface verbatim (see reportInviteeFlowError);
   * these are the human sentences shown in their place.
   */
  errors: {
    step: 'Something went wrong. Please try again.',
    accept: 'We couldn’t accept this invite. Please try again.',
    decline: 'We couldn’t decline this invite. Please try again.',
    complete: 'We couldn’t finish setting things up. Please try again.',
  },
} as const;
